import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Share,
  Alert,
  Platform,
  ActivityIndicator
} from "react-native";
import { useTranslation } from 'react-i18next';
// import TransferSummaryModal from "./TransferSummaryModal";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { FeeLookupHelper } from "../../../../../helper/feeLookupHelper";
import { sharePayload } from "../../../../uiComponents/ShareUtils";
import CopyIcon from "../../../../../../../../assets/images/copy.svg";
import ProcessingIcon from "../../../../../../../../assets/images/processing.svg";
import CalculatorIcon from "../../../../../../../../assets/images/calculator.svg";
import CashIcon from "../../../../../../../../assets/images/cash.svg";
import CoinsSwapIcon from "../../../../../../../../assets/images/coins-swap.svg";
import GraphUpIcon from "../../../../../../../../assets/images/graph-up.svg";
import PiggyBankIcon from "../../../../../../../../assets/images/piggy-bank.svg";
import Colors from "../../../../../constants/colors";
import copyToClipboard from "../../../../../helper/copyToClipBoard";
import AnimatedSuccessIcon from "../../../common/AnimatedSuccessView";
import { capturePosthogEvent } from "../../../../../utils/analytics";

// Example props interface
interface HeadToCashierStepProps {
  status?: string; // e.g. "Staged"
  receiveAmount?: string;
  receiveCurrency?: string;
  onViewSummary?: () => void;
  onShareReceipt?: () => void;
}

const HeadToCashierStep: React.FC<HeadToCashierStepProps> = ({
  receiveAmount,
  receiveCurrency,
  status,
  onViewSummary,
  // onShareReceipt,
}) => {
  const { t } = useTranslation();
  const displayStatus = status ?? t("STATUS_STAGED");
  const kioskId = useSessionStore((s: SessionState) => (s as any).kioskId);
  const [summaryVisible, setSummaryVisible] = React.useState(false);
  const [sharing, setSharing] = useState(false);

  // Pull values from store
  const pendingSendOption = useSessionStore((s: SessionState) => (s as any).pendingSendOption);
  const selectedSendOption = useSessionStore((s: SessionState) => (s as any).selectedSendOption);
  const receiverFirstName = useSessionStore((s: SessionState) => (s as any).receiverFirstName);
  const receiverMiddleName = useSessionStore((s: SessionState) => (s as any).receiverMiddleName);
  const receiverLastName = useSessionStore((s: SessionState) => (s as any).receiverLastName);
  const receiverLastName2 = useSessionStore((s: SessionState) => (s as any).receiverLastName2);
  const confirmationNumber = useSessionStore((s: SessionState) => (s as any).confirmationNumber);

  const option: any = pendingSendOption || selectedSendOption || null;

  const storeReceiveCurrency = option ? FeeLookupHelper.getReceiveCurrency(option) : "";
  const receiveAmountNum = option ? FeeLookupHelper.getReceiveAmount(option) : 0;
  const amount = useMemo(() => {
    const num = Number(receiveAmountNum || 0);
    return isFinite(num) ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00";
  }, [receiveAmountNum]);
  const receiverName = useMemo(() =>
    [receiverFirstName, receiverMiddleName, receiverLastName, receiverLastName2].filter(Boolean).join(" "),
    [receiverFirstName, receiverMiddleName, receiverLastName, receiverLastName2]
  );
  const referenceNumber = confirmationNumber || "";

  // Additional computed values for modal
  const sendCurrency = (option && (option.SendAmounts?.SendCurrency || "USD")) || "USD";
  const sendAmountNum = Number(option?.SendAmounts?.SendAmount ?? 0);
  const exchangeRateNum = option ? FeeLookupHelper.getExchangeRate(option) : 0;
  const feesNum = Number(option?.SendAmounts?.TotalDiscountAmount || 0) + Number(option?.SendAmounts?.TotalSendFees || 0);
  const totalNum = Number(option?.TotalAmount || (sendAmountNum + feesNum));
  const state = useSessionStore.getState() as any;
  const referenceNumberSearchResult = (state.referenceNumberSearchResult || {}) as any;
  const hasLoggedEvent = useRef(false);

  console.log('referenceNumberSearchResult==>', referenceNumberSearchResult)

  useEffect(() => {
    if (!hasLoggedEvent.current) {
      hasLoggedEvent.current = true;
      capturePosthogEvent('Receive Flow - Head to Cashier Step', {
        'data-mg-reference-number': referenceNumber
      });
    }
  }, [referenceNumber]);

  const buildReceiptText = () =>
    t("SHARE_RECEIPT_MESSAGE", {
      receiverName,
      amount: receiveAmount,
      currency: receiveCurrency,
      referenceNumber,
      statusLabel: t("STATUS"),
      statusValue: displayStatus,
    });


  const onShare = useCallback(async () => {
    setSharing(true);
    try {
      const payload = {
        title: t("MONEYGRAM_RECEIPT_TITLE"),
        message: buildReceiptText(),
        // If you have a receipt URL, add it here (helps web share)
        // url: 'https://app.example.com/receipt/0000123456'
      };

      const result = await sharePayload(payload);
      // result has meta info about which path was used
      if (Platform.OS === "web") {
        if ((result as any).usedWebShare) {
          // navigator.share resolved — nothing else to do
          console.log("Shared via navigator.share", result);
        } else if ((result as any).usedClipboard) {
          Alert.alert(
            t("INFO_SHARE_IS_NOT_AVAILABLE"),
            t("INFO_SHARE_SHEET_IS_NOT_SUPPORTED_TO_BROWSER")
          );
        } else {
          Alert.alert(t("INFOR_SHARED"), t("INFO_SHARING_COMPLETED"));
        }
      } else {
        // Native platforms
        console.log("Native share result:", result);
      }
    } catch (err: any) {
      Alert.alert(t("ERROR_UNABLE_TO_SHARE"), err?.message || t("ERROR_AN_ERROR_OCCURRED_WHILE_SHARING"));
    } finally {
      setSharing(false);
    }
  }, [receiverName, receiveAmount, receiveCurrency, referenceNumber, displayStatus, t]);

//   const normalizeAmount = (amount: string) => {
//   return amount.includes(".") ? amount : amount + ".00";
// };

//   const receiveAmountToDisplay = normalizeAmount(referenceNumberSearchResult?.ReceiveAmount)
  return (
    <View style={styles.container}>
      {/* Icon or Circle Avatar */}
      <View style={{alignItems: 'center'}}>
        <AnimatedSuccessIcon />
        {/* <Image source={SuccessIcon} style={styles.successIconStyle} /> */}
        <Text style={styles.title}>{t("HEAD_TO_CASHIER")}</Text>
        <Text style={styles.subtitle}>{t("THANK_YOU_MONEYGRAM")}</Text>

        {/* Receiver amount */}
        <Text style={styles.receiverLine}>
          {t("YOU_WILL_RECEIVE")}
        </Text>
        <Text style={styles.amount}>{referenceNumberSearchResult?.ReceiveAmount.toFixed(2)}<Text style={styles.currencyStyle}>{referenceNumberSearchResult?.ReceiveCurrency}</Text></Text>

        {/* Status */}
        <Text style={[styles.referenceLabel, { marginBottom: 9}]}>
          {t("STATUS")} <Text style={styles.statusValue}>{t("AVAILABLE_FOR_PICKUP")}</Text>
        </Text>
      </View>
      {/* Reference Number */}
      <View style={styles.referenceRow}>
        <Text style={styles.referenceLabel}>{t("REFERENCE")}</Text>
        <TouchableOpacity style={styles.referenceCopy} onPress={() => copyToClipboard(referenceNumber)} testID="referenceNumberCopy">
          <Text style={styles.referenceNumber} testID="receiveReferenceNumber">{referenceNumberSearchResult?.ReferenceNumber}</Text>
          {/* You can replace with an icon */}
          <CopyIcon width={24} height={24}/>
        </TouchableOpacity>
      </View>

      {/* Instruction */}
      <Text style={styles.instructionTitle}>{t("WHAT_DO_I_SAY")}</Text>
      <Text style={styles.instruction}>
        {t("LET_THE_CASHIER_KNOW")}
      </Text>

      {/* Transfer Details */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t("TRANSFER_DETAILS")}</Text>
            <View style={styles.sectionBlock}>
              <ProcessingIcon width={24} height={24}/>
              <View style={{ marginLeft: 20}}>
                <Text style={styles.label}>{t("STATUS_PROCESSING")}</Text>
                <Text style={styles.value}>{t("TRANSFER_COMPLETE")}</Text>
              </View>
            </View>
             <View style={styles.sectionBlock}>
              <CoinsSwapIcon width={24} height={24}/>
              <View style={{ marginLeft: 20}}>
                <Text style={styles.label}>{t("WITHDRAWAL_METHOD")}</Text>
                <Text style={styles.value}>{t("CASH_AT_LOCATION")}</Text>
              </View>
            </View>

            <View style={styles.sectionBlock}>
              <GraphUpIcon width={24} height={24}/>
              <View style={{ marginLeft: 20}}>
                <Text style={styles.label}>{t("EXHANGE_RATE")}</Text>
                <Text style={styles.value}>{referenceNumberSearchResult?.OriginalExchangeRate} {referenceNumberSearchResult?.OriginalSendCurrency}</Text>
              </View>
            </View>

            <View style={styles.sectionBlock}>
              <PiggyBankIcon width={24} height={24}/>
              <View style={{ marginLeft: 20}}>
                <Text style={styles.label}>{t("FEES")}</Text>
                <Text style={styles.value}>{referenceNumberSearchResult?.OriginalSendFee} {referenceNumberSearchResult?.OriginalSendCurrency}</Text>
              </View>
            </View>

            <View style={styles.sectionBlock}>
              <CashIcon width={24} height={24}/>
              <View style={{ marginLeft: 20}}>
                <Text style={[styles.label, { color: Colors.black}]}>{t("YOU_WITHDRAW")}</Text>
                <Text style={styles.amountText}>{referenceNumberSearchResult?.ReceiveAmount.toFixed(2)} <Text style={styles.currencyText}>{referenceNumberSearchResult?.ReceiveCurrency}</Text></Text>
              </View>
            </View>

            <View style={styles.sectionBlock}>
              <CalculatorIcon width={24} height={24}/>
              <View style={{ marginLeft: 20}}>
                <Text style={[styles.label, { color: Colors.black}]}>{t("TOTAL_INCLUDES_FESS")}</Text>
                <Text style={styles.amountText}>{referenceNumberSearchResult?.ReceiveAmount.toFixed(2)} <Text style={styles.currencyText}>{referenceNumberSearchResult?.ReceiveCurrency}</Text></Text>
              </View>
            </View>
          </View>
     
        {sharing && (
        <View style={styles.overlay}>
          <ActivityIndicator size="large" color="#fff" />
          <Text style={styles.overlayText}>Preparing share …</Text>
        </View>
      )}

      {/* The Continue / I'm Finished button will be rendered by MainSendScreen */}
    </View>
  );
};

export default HeadToCashierStep;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 50
  },
  successIconStyle: {
    width: 80,
    height: 80,
    marginBottom: 24,
  },
  title: {
    fontSize: 28,
    color: Colors.darkGrey,
    fontWeight: '500',
    fontFamily: 'Rubik',
    lineHeight: 34,
    marginBottom: 8,
    marginTop: 24
  },
  subtitle: {
    fontSize: 16,
    color: Colors.black,
    fontWeight: '400',
    fontFamily: 'Rubik',
    marginBottom: 32,
  },
  receiverLine: {
    fontSize: 16,
    color: Colors.black,
    fontWeight: '500',
    fontFamily: 'Rubik',
    lineHeight: 20,
    marginBottom: 6,
    textAlign: "center",
    width: "100%",
  },
  amount: {
    fontSize: 40,
    color: Colors.titleTextColor,
    fontWeight: '500',
    fontFamily: 'Rubik',
    marginBottom: 6,
    lineHeight: 46,
    textAlign: "center",
    width: "100%",
  },
  currencyStyle:{
    fontSize: 24,
    fontFamily: 'Rubik',
    fontWeight: '500',
    color: Colors.infoSubValue,
    lineHeight: 30,
    paddingLeft: 8
  },
  statusValue: {
    fontSize: 16,
    color: Colors.termsAndConditionsBlue,
    fontWeight: '400',
    fontFamily: 'Rubik',
    lineHeight: 26,
    marginRight: 12,
  },
  referenceRow: {
    flexDirection: "row",
    justifyContent: 'center',
    alignItems: "center",
    marginBottom: 36,
    backgroundColor: Colors.buttonTextColor,
    borderRadius: 8,
    paddingVertical: 12,
  },
  referenceLabel: {
    fontSize: 16,
    color: Colors.darkGrey,
    fontWeight: '500',
    fontFamily: 'Rubik',
    lineHeight: 24,
    marginRight: 12,
  },
  referenceCopy: {
    flexDirection: "row",
    alignItems: "center",
  },
  referenceNumber: {
    fontSize: 16,
    color: Colors.termsAndConditionsBlue,
    fontWeight: '400',
    fontFamily: 'Rubik',
    lineHeight: 26,
    marginRight: 12,
  },
  copyIcon: {
    marginLeft: 4,
    fontSize: 14,
  },
  instructionTitle: {
    fontSize: 20,
    color: Colors.darkGrey,
    lineHeight: 26,
    marginBottom: 8,
    fontFamily: 'Rubik',
    fontWeight: '500',
    textAlign: 'left'
  },
  instruction: {
    fontSize: 16,
    color: Colors.black,
    marginBottom: 24,
    fontFamily: 'Rubik',
    fontWeight: '400'
    // paddingHorizontal: 12,
  },
  actionsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 24,
    width: "100%",
  },
  actionBox: {
    flex: 1,
    alignItems: "center",
    padding: 12,
    marginHorizontal: 6,
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 8,
  },
  receiptView: {
     width: 40,
    height: 40,
    alignItems: "center",
    padding: 12,
    backgroundColor: '#1F1F1F',
    marginBottom: 12,
    // borderWidth: 1,
    // borderColor: "#ddd",
    borderRadius: 30,
  },
  actionIcon: {
    fontSize: 20,
    marginBottom: 6,
  },
  actionLabel: {
    fontSize: 13,
    textAlign: "center",
    color: "#333",
  },
  shareBtn: {
    marginTop: 20,
    backgroundColor: "#000",
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
  },
  shareText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
  finishText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "700",
  },
  overlay: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    alignItems: "center",
  },
  overlayText: {
    color: "#fff",
    marginTop: 10,
  },
  section: {
    width: "100%",
    borderTopWidth: 1,
    borderTopColor: "#ddd",
    paddingVertical: 36,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: "500",
    marginBottom: 12,
    fontFamily: 'Rubik',
    lineHeight: 24,
    color: Colors.black,
  },
  sectionBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
  },
  leftIcons: {
    width: 24,
    height: 24,
    marginRight: 20
  },
  label: {
    fontSize: 16,
    fontWeight: "500",
    marginBottom: 4,
    fontFamily: 'Rubik',
    lineHeight: 24,
    color: Colors.darkGrey,
  },
  value: {
    fontSize: 14,
    fontWeight: "400",
    fontFamily: 'Rubik',
    lineHeight: 16,
    color: Colors.infoSubValue,
  },
  amountText: {
    fontSize: 24,
    fontWeight: "500",
    fontFamily: 'Rubik',
    lineHeight: 16,
    color: Colors.black,
  },
  currencyText: {
    fontSize: 16,
    fontWeight: "500",
    fontFamily: 'Rubik',
    lineHeight: 16,
    color: Colors.subTitleColor,
  }
});